import React, { useMemo } from 'react';
import classnames from 'classnames';
import {
  Typography,
  SidebarIcon,
  navigationItems,
  APP_ROUTE_PREFIXES,
  useCrossAppNavigate,
} from '@lucid/lego';
import styles from './QuickLinks.module.scss';
import { isNavigationEnabled } from '../../../config/featureFlags';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';

type QuickLinksProps = {
  theme: 'light' | 'dark';
  labels?: Record<string, string>;
};

const QuickLinks: React.FC<QuickLinksProps> = ({ theme, labels }) => {
  const navigate = useLocaleNavigate();
  const locale = useStore((state) => state.locale);
  const workspaces = useMemo(
    () => navigationItems.filter((workspace) => isNavigationEnabled(workspace.id)),
    []
  );

  const handleNavigate = useCrossAppNavigate(APP_ROUTE_PREFIXES.partnerPortal, locale, navigate);

  return (
    <div className={styles.tocWrapper} data-theme={theme}>
      <div className={styles.workspacesGrid}>
        {workspaces.map((workspace, index) => (
          <div
            key={workspace.id}
            className={classnames(
              styles.workspaceColumn,
              index % 2 === 0 ? styles.borderPrimary : styles.borderWhite
            )}
          >
            <div className={styles.workspaceHeader}>
              {workspace.icon && (
                <SidebarIcon name={workspace.icon.name} size={20} color="#d7be96" />
              )}
              <Typography
                variant="body"
                size="large"
                color="primary-1"
                theme={theme}
                className={styles.workspaceTitle}
              >
                {labels?.[`sidebar.${workspace.id}`] ?? workspace.label}
              </Typography>
            </div>
            <ul className={styles.childrenList}>
              {workspace.childrens?.map((child) => (
                <li key={child.id}>
                  <a
                    href={child.path}
                    className={styles.childLink}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigate(child.path);
                    }}
                  >
                    {child.icon && (
                      <SidebarIcon
                        name={child.icon.name}
                        size={16}
                        color={theme === 'dark' ? '#9CA3AF' : '#6B7280'}
                      />
                    )}
                    <Typography variant="body" size="medium" color="white" theme={theme}>
                      {labels?.[`sidebar.${workspace.id}.${child.id}`] ?? child.label}
                    </Typography>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickLinks;
